// src/common/loginApi.js
import { sendRequest } from '@/common/sendRequest';
import userUtils from '@/common/userUtils';
import { useMainStore } from "@/store/mainStore";
import { logDebug, logError, logInfo } from '@/utils/logHelper';

// ✅ cacheBuster에서 필요한 함수 import
import {
  clearCookiesAggressive,
  clearLocal,
  clearSession,
  clearAppIndexedDB,
  clearViaServer,
} from '@/utils/cacheBuster';

const loginApi = {
  /**
   * 로그인 요청
   * @param {object} data - 예: { email, password }
   */
  async login(data) {
    // 기존 토큰 제거
    userUtils.clearAccessToken();
    userUtils.clearRefreshToken();
    userUtils.clearUserInfo();

    // 서버에 로그인 요청
    const response = await sendRequest('/api/public/security/login', {
      method: 'POST',
      data,
    });

    logDebug('login 결과:', response);

    // 토큰 및 유저정보 저장
    userUtils.setLoginResponse(response);

    // Pinia 스토어 사용
    const mainStore = useMainStore();
    mainStore.SET_IS_LOGIN(true);

    // 사용자 로그인 이벤트 디스패치
    window.dispatchEvent(new CustomEvent('user-logged-in'));

    return response;
  },

  /**
   * 로그아웃 요청
   * @param {Object} options - 로그아웃 옵션
   * @param {boolean} options.clearAllData - 모든 사이트 데이터 삭제 여부 (기본: true)
   * @param {boolean} options.useServerHeader - 서버 Clear-Site-Data 사용 여부 (기본: false)
   * @param {boolean} options.silent - 에러 시 조용히 처리 (기본: true)
   */
 // src/common/loginApi.js - logout 메서드 수정

/**
 * 로그아웃 요청
 * @param {Object} options - 로그아웃 옵션
 * @param {boolean} options.clearAllData - 모든 사이트 데이터 삭제 여부 (기본: false → sessionStorage만)
 * @param {boolean} options.useServerHeader - 서버 Clear-Site-Data 사용 여부 (기본: false)
 * @param {boolean} options.silent - 에러 시 조용히 처리 (기본: true)
 */
async logout(options = {}) {
  const {
    clearAllData = false,  // ✅ 변경: true → false
    useServerHeader = false,
    silent = true,
  } = options;

  const results = {
    server: null,
    client: null,
  };

  // 1단계: 서버 로그아웃 요청
  try {
    await sendRequest('/api/public/security/logout', {
      method: 'POST',
    });
    results.server = { success: true };
    logInfo('서버 로그아웃 완료');
  } catch (error) {
    results.server = { success: false, error: error.message };
    logError('서버 로그아웃 실패:', error);
  }

  // 2단계: 클라이언트 데이터 정리
  try {
    // 토큰 제거 (항상 실행)
    userUtils.clearAccessToken();
    userUtils.clearRefreshToken();
    userUtils.clearUserInfo();

    // ✅ 기본: sessionStorage만 정리
    if (clearAllData) {
      const clearResults = await this._clearClientData({ useServerHeader, silent });
      results.client = clearResults;
    } else {
      sessionStorage.clear();
      results.client = { sessionStorage: true };
    }

    // Pinia 스토어 상태 업데이트
    const mainStore = useMainStore();
    mainStore.SET_IS_LOGIN(false);

    // 로그아웃 이벤트 디스패치
    window.dispatchEvent(new CustomEvent('user-logged-out'));

    logInfo('클라이언트 로그아웃 완료', results.client);
  } catch (error) {
    results.client = { success: false, error: error.message };
    logError('클라이언트 데이터 정리 실패:', error);
    
    if (!silent) {
      throw error;
    }
  }

  return results;
},

  /**
   * 클라이언트 데이터 정리 (내부 헬퍼)
   * @private
   */
  async _clearClientData(options = {}) {
    const { useServerHeader = false, silent = true } = options;
    const results = {};

    // Promise.allSettled로 병렬 실행 (하나 실패해도 나머지 계속)
    const tasks = [
      { name: 'cookies', fn: clearCookiesAggressive },
      { name: 'localStorage', fn: clearLocal },
      { name: 'sessionStorage', fn: clearSession },
      { name: 'indexedDB', fn: clearAppIndexedDB },
    ];

    // 서버 Clear-Site-Data 옵션
    if (useServerHeader) {
      tasks.push({ name: 'serverHeader', fn: clearViaServer });
    }

    const settled = await Promise.allSettled(
      tasks.map(async (task) => {
        try {
          const result = await task.fn();
          return { name: task.name, ...result };
        } catch (err) {
          if (!silent) {
            logError(`${task.name} 정리 실패:`, err);
          }
          return { name: task.name, error: err.message };
        }
      })
    );

    // 결과 정리
    settled.forEach((item, idx) => {
      const taskName = tasks[idx].name;
      if (item.status === 'fulfilled') {
        results[taskName] = item.value;
      } else {
        results[taskName] = { error: item.reason?.message || 'Unknown error' };
      }
    });

    return results;
  },

  /**
   * 강제 로그아웃 (토큰 만료 등)
   * 서버 요청 없이 클라이언트만 정리
   */
  async forceLogout(options = {}) {
    const { clearAllData = true, silent = true } = options;

    logInfo('강제 로그아웃 실행');

    // 토큰 제거
    userUtils.clearAccessToken();
    userUtils.clearRefreshToken();
    userUtils.clearUserInfo();

    // 데이터 정리
    if (clearAllData) {
      await this._clearClientData({ useServerHeader: false, silent });
    } else {
      sessionStorage.clear();
    }

    // Pinia 스토어 상태
    const mainStore = useMainStore();
    mainStore.SET_IS_LOGIN(false);

    // 이벤트 디스패치
    window.dispatchEvent(new CustomEvent('user-force-logged-out'));

    return { forced: true };
  },

  /**
   * 토큰 검증 요청
   */
  async verifyToken(token) {
    try {
      const response = await sendRequest('/api/public/security/verifyToken', {
        method: 'POST',
        token,
      });
      logDebug('verifyToken 결과:', response);
      return response;
    } catch (error) {
      logError('verifyToken 실패:', error);
      throw error;
    }
  },
};

export default loginApi;